import { Box, Button, Chip, Container, IconButton, Paper, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMuiModal } from '../../_core/hooks';
import { DistrictDto, EngagementStatusDto, FieldOptionInterface, ProviderDto } from '@families-link/shared';
import { LanguagePieChart } from '../components/language-pie-chart/language-pie-chart.component';
import {
	useGetAnalyticsTotalStatisticsQuery,
	useGetAnalyticsLanguageDistributionQuery,
	useGetSurveyEngagementFunnelQuery,
} from '../../../store/api/analytics.api';
import { ChipWithoutBorderCloseIcon } from '../../_core/components/icons';
import { chipFilterStyles } from '../../_core/styles';
import { EngagStats, SendIcon, Exclamation } from '../../_core/constants/icons.constants';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ZoomOutMapRoundedIcon from '@mui/icons-material/ZoomOutMapRounded';
import { EngagementFullDateChart } from '../components/engagement-full-data-chart/engagement-full-data-chart.component';
import { ProviderFullDateChart } from '../components/provider-full-data-chart/provider-full-data-chart.component';
import dayjs from 'dayjs';
import { CoreButton } from '../../_core/components/_ui/core-button';
import {
	AnalyticsFiltersModal,
	AnalyticsFiltersModalProps,
} from '../components/filters-modal-form/filters-modal-form.component';
import { format } from 'date-fns';
import { FilterIconComponent } from '../../_core/components/_ui/core-autocomplete/filter-icon.component';
import { useAnalyticsFilters } from '../hooks/use-analytics-filters.hook';
import { inverseTransformFilterValues } from '../utils/inverseTransformFilterValues.util';
import { FadeSpinner } from '../../_core/components/_ui/fade-spinner';
import { useTheme } from '@mui/material/styles';
import { SurveyEngagementFunnelLinearChart } from '../components/survey-engagement-funnel-linear-chart/survey-engagement-funnel-linear-chart.component';
import { EngagementStatusByLanguageChart } from '../components/engagement-status-by-language-chart/engagement-status-by-language-chart.component';
import { SurveyTrendFullDateChart } from '../components/survey-trend-over-time-chart/survey-trend-over-time-full-data-chart.component';
import { TotalLanguagesModal } from '../components/total-languages-modal/total-languages-modal.component';
import { AnalyticsFilters } from '../../_core/interfaces/analytics-filters.interface';

export type FilterValuesArrays = {
	districts: DistrictDto[];
	engagementType: string[];
	duration: Date[] | null[];
	schools: FieldOptionInterface[];
	programs: FieldOptionInterface[];
	services: FieldOptionInterface[];
	providers: ProviderDto[];
	languages: string[];
	engagementStatus: EngagementStatusDto[];
};

export type FilterValuesStrings = {
	districtIds: string;
	engagementTypes: string;
	duration: (Date | string | null)[];
	schoolIds: string;
	programIds: string;
	serviceIds: string;
	providerIds: string;
	languages: string;
	engagementStatuses: string;
};

export type AnalyticsPageProps = {
	isAdminRoute: boolean;
	currentDistrictId?: string | null;
	currentDistrict?: DistrictDto;
	transformedFilterValues?: FilterValuesStrings;
	isReadOnly?: boolean;
	isPublic?: boolean;
	authToken?: string;
};

type AnalyticsStatsRowProps = {
	label: string;
	icon: React.ReactNode;
	value: number | string;
};

function TotalStatsLoader() {
	return (
		<Box
			sx={{
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '100%',
			}}
		>
			<FadeSpinner size={31} height={10} width={4} borderRadius={3} />
		</Box>
	);
}

export const AnalyticsPage: FC<AnalyticsPageProps> = (props) => {
	const {
		isAdminRoute,
		currentDistrictId,
		currentDistrict,
		transformedFilterValues: externalTransformedFilters,
		isReadOnly = false,
		isPublic = false,
		authToken,
	} = props;

	const [t] = useTranslation();
	const appTheme = useTheme();

	const today = new Date();
	const last30Days = new Date();
	last30Days.setDate(today.getDate() - 30);

	const initialFilterValues = {
		districts: currentDistrict ? [currentDistrict] : [],
		engagementType: [],
		duration: [last30Days, today],
		schools: [],
		programs: [],
		services: [],
		providers: [],
		languages: [],
		engagementStatus: [],
	};

	const initialTransformedFilterValues = {
		districtIds: currentDistrictId ?? '',
		engagementTypes: '',
		duration: [last30Days, today],
		schoolIds: '',
		programIds: '',
		serviceIds: '',
		providerIds: '',
		languages: '',
		engagementStatuses: '',
	};

	const [filterValues, setFilterValues] = useState<FilterValuesArrays>(() => {
		if (externalTransformedFilters) {
			return inverseTransformFilterValues(externalTransformedFilters, currentDistrict);
		}
		return initialFilterValues;
	});

	const [transformedFilterValues, setTransformedFilterValues] = useState<FilterValuesStrings>(
		externalTransformedFilters ?? initialTransformedFilterValues
	);

	const { setAnalyticsFilters } = useAnalyticsFilters();

	useEffect(() => {
		if (currentDistrict) {
			setFilterValues((prevState) => ({ ...prevState, districts: [currentDistrict] }));
		}
	}, [currentDistrict]);

	useEffect(() => {
		const transformed = transformFilterValues(filterValues);

		const transformedWithStrings = {
			...transformed,
			duration: transformed.duration.map((d) => (d instanceof Date ? d.toISOString() : '')),
		};
		setTransformedFilterValues(transformFilterValues(filterValues));
		setAnalyticsFilters(transformedWithStrings);
	}, [filterValues]);

	const [openTotalLanguagesModal, setOpenTotalLanguagesModal] = useState(false);

	const { openModal, closeModal } = useMuiModal<AnalyticsFiltersModalProps>(AnalyticsFiltersModal);

	const handleOpenFiltersModal = useCallback(() => {
		openModal({
			isAdminRoute: isAdminRoute,
			currentDistrict: currentDistrict,
			defaultValues: filterValues,
			submitAction: setFilterValues,
			closeModal: closeModal,
		});
	}, [openModal, filterValues]);

	const handleDeleteChip = (fieldName: string, id?: string) => {
		switch (fieldName) {
			case 'duration':
				setFilterValues((prevValue) => ({ ...prevValue, duration: [null, null] }));
				break;
			case 'engagementType':
				setFilterValues((prevValue) => ({
					...prevValue,
					engagementType: prevValue.engagementType.filter((engagementType: string) => engagementType !== id),
				}));
				break;
			case 'districts':
				setFilterValues((prevValue) => ({
					...prevValue,
					districts: prevValue.districts.filter((district: DistrictDto) => district._id !== id),
				}));
				break;
			case 'schools':
				setFilterValues((prevValue) => ({
					...prevValue,
					schools: prevValue.schools.filter((school: FieldOptionInterface) => school._id !== id),
				}));
				break;
			case 'services':
				setFilterValues((prevValue) => ({
					...prevValue,
					services: prevValue.services.filter((service: FieldOptionInterface) => service._id !== id),
				}));
				break;
			case 'providers':
				setFilterValues((prevValue) => ({
					...prevValue,
					providers: prevValue.providers.filter((provider: ProviderDto) => provider._id !== id),
				}));
				break;
			case 'languages':
				setFilterValues((prevValue) => ({
					...prevValue,
					languages: prevValue.languages.filter((language: string) => language !== id),
				}));
				break;
			case 'programs':
				setFilterValues((prevValue) => ({
					...prevValue,
					programs: prevValue.programs.filter((program: FieldOptionInterface) => program._id !== id),
				}));
				break;
			case 'engagementStatus':
				setFilterValues((prevValue) => ({
					...prevValue,
					engagementStatus: prevValue.engagementStatus.filter(
						(engagementStatus: EngagementStatusDto) => engagementStatus._id !== id
					),
				}));
				break;
			default:
				break;
		}
	};

	const normalArgs: AnalyticsFilters = {
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(filterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(filterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	const authenticatedNormalArgs = useMemo(
		() => ({
			queryParams: normalArgs,
			authToken,
		}),
		[normalArgs, authToken]
	);

	const languageNormalQuery = useGetAnalyticsLanguageDistributionQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const languageData = languageNormalQuery.data;
	const isFetchingLanguagePieChart = languageNormalQuery.isFetching;

	const statisticNormalQuery = useGetAnalyticsTotalStatisticsQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const dashboardStats = statisticNormalQuery.data;
	const isFetching = statisticNormalQuery.isFetching;

	const dashboardStatsData: AnalyticsStatsRowProps[] = [
		{
			label: 'Total Families',
			icon: <EngagStats width="24px" />,
			value: dashboardStats?.totalFamilies ?? 0,
		},
		{
			label: 'Survey Not Sent',
			icon: <CheckCircleOutlinedIcon sx={{ color: appTheme.colors.lightBlue, fontSize: '24px' }} />,
			value: dashboardStats?.totalNotSentSurveys ?? 0,
		},
		{
			label: 'Engagement Rate',
			icon: <SendIcon width="24px" />,
			value: dashboardStats?.engagementRate ? `${Number(dashboardStats.engagementRate.toFixed(1))}%` : '0%',
		},
		{
			label: 'Survey Response Rate',
			icon: <Exclamation width="24px" style={{ color: appTheme.colors.lightBlue }} />,
			value: dashboardStats?.surveyResponseRate ? `${Number(dashboardStats.surveyResponseRate.toFixed(1))}%` : '0%',
		},
		{
			label: 'Total Languages',
			icon: <LanguageOutlinedIcon sx={{ color: appTheme.colors.lightBlue, fontSize: '24px' }} />,
			value: dashboardStats?.totalLanguages ?? 0,
		},
	];

	const surveyEngagementFunnel = useGetSurveyEngagementFunnelQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const surveyEngagementFunnelData = surveyEngagementFunnel.data;
	const isFetchingSurveyEngagementFunnel = surveyEngagementFunnel.isFetching;

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateRows: 'auto 280px 400px 400px',
				width: '100%',
				height: '100%',
				gap: 1,
				overflowY: 'auto',
			}}
		>
			<Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
				<Paper
					elevation={0}
					sx={{
						width: '100%',
						display: 'flex',
						borderRadius: '16px',
						minHeight: '56px',
						px: '12px',
						py: 1,
						gap: 2,
						alignItems: 'center',
					}}
				>
					{!isPublic && (
						<Box
							sx={{
								minWidth: '90%',
								maxWidth: 'calc(100% - 38px)',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								flexWrap: 'wrap',
							}}
						>
							{filterValues.duration?.length && filterValues.duration[0] && filterValues.duration[1] ? (
								<Chip
									sx={chipFilterStyles}
									variant="outlined"
									label={`${format(filterValues.duration[0], 'MM.dd.yyyy')} - ${format(
										filterValues.duration[1],
										'MM.dd.yyyy'
									)}`}
									deleteIcon={<ChipWithoutBorderCloseIcon />}
									onDelete={() => {
										handleDeleteChip('duration');
									}}
								/>
							) : null}
							{filterValues.engagementType?.length
								? filterValues.engagementType.map((engagementType: string) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={engagementType}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('engagementType', engagementType);
											}}
										/>
								  ))
								: null}
							{isAdminRoute
								? filterValues.districts?.length
									? filterValues.districts.map((district: DistrictDto) => (
											<Chip
												sx={chipFilterStyles}
												variant="outlined"
												label={district.name}
												deleteIcon={<ChipWithoutBorderCloseIcon />}
												onDelete={() => {
													handleDeleteChip('districts', district._id);
												}}
											/>
									  ))
									: null
								: null}

							{filterValues.schools?.length
								? filterValues.schools.map((school: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={school.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('schools', school._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.services?.length
								? filterValues.services.map((service: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={service.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('services', service._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.providers?.length
								? filterValues.providers.map((provider: ProviderDto) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={provider.fullName}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('providers', provider._id);
											}}
										/>
								  ))
								: null}
							{filterValues.languages?.length
								? filterValues.languages.map((language: string) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={language}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('languages', language);
											}}
										/>
								  ))
								: null}
							{filterValues.programs?.length
								? filterValues.programs.map((program: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={program.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('programs', program._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.engagementStatus?.length
								? filterValues.engagementStatus.map((engagementStatus: EngagementStatusDto) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={engagementStatus.statusName}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('engagementStatus', engagementStatus._id);
											}}
										/>
								  ))
								: null}
						</Box>
					)}
					{!isPublic && (
						<CoreButton
							variant="info-light"
							contraModeStyleButton
							onClick={handleOpenFiltersModal}
							sx={{ ml: 'auto', borderRadius: '12px', p: 1 }}
						>
							<FilterIconComponent sx={{ stroke: 'white' }} />
						</CoreButton>
					)}
					{isPublic && (
						<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							<Typography sx={{ fontSize: '24px', fontWeight: 500, px: 2, py: 1 }}> {t('nav.analytics')}</Typography>
							{filterValues.duration?.[0] && filterValues.duration?.[1] && (
								<Typography sx={{ fontSize: '16px', fontWeight: 500, color: (theme) => theme.colors.grayLightBlue }}>
									{`${format(filterValues.duration[0], 'MM.dd.yyyy')} - ${format(
										filterValues.duration[1],
										'MM.dd.yyyy'
									)}`}
								</Typography>
							)}
						</Box>
					)}
				</Paper>
			</Box>

			<Box
				sx={{
					display: 'flex',
					width: '100%',
					height: '280px',
					gap: 1,
				}}
			>
				<Box
					sx={{
						display: 'grid',
						gridTemplateRows: `repeat(${dashboardStatsData.length}, minmax(0, 1fr))`,
						gap: 1,
						width: '100%',
						height: '100%',
					}}
				>
					{dashboardStatsData.map((row) => (
						<Paper elevation={0} sx={{ borderRadius: '16px', height: '100%' }}>
							{isFetching ? (
								<TotalStatsLoader />
							) : (
								<Box
									sx={{
										display: 'flex',
										height: '100%',
										flexDirection: 'row',
										justifyContent: 'space-between',
										alignItems: 'center',
										width: '100%',
										px: 2,
									}}
								>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											gap: 1,
										}}
									>
										{row.icon}
										<Typography
											sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}
										>
											{row.label}
										</Typography>
									</Box>
									{row.label === 'Total Languages' ? (
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'row',
												justifyContent: 'flex-end',
												gap: 1.5,
												alignItems: 'center',
											}}
										>
											<Typography sx={{ color: (theme) => theme.colors.lightBlue, fontSize: '28px', fontWeight: 500 }}>
												{row.value}
											</Typography>
											<IconButton sx={{ p: 0 }} onClick={() => setOpenTotalLanguagesModal(true)}>
												<ZoomOutMapRoundedIcon
													sx={{ fontSize: '28px', fontWeight: 500, color: (theme) => theme.colors.lightBlue }}
												/>
											</IconButton>
											<TotalLanguagesModal
												open={openTotalLanguagesModal}
												filters={normalArgs}
												onClose={() => setOpenTotalLanguagesModal(false)}
												authToken={authToken}
											></TotalLanguagesModal>
										</Box>
									) : (
										<Typography sx={{ color: (theme) => theme.colors.lightBlue, fontSize: '28px', fontWeight: 500 }}>
											{row.value}
										</Typography>
									)}
								</Box>
							)}
						</Paper>
					))}
				</Box>

				<Paper
					elevation={0}
					sx={{
						width: '100%',
						height: '100%',
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Box sx={{ height: '100%', flex: 1, py: 2, px: 3 }}>
						<SurveyEngagementFunnelLinearChart
							data={surveyEngagementFunnelData ? surveyEngagementFunnelData : []}
							loading={isFetchingSurveyEngagementFunnel}
						/>
					</Box>
				</Paper>

				<Paper
					elevation={0}
					sx={{
						height: '100%',
						width: '100%',
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Box sx={{ height: '100%', flex: 1, py: 2, px: 3 }}>
						<LanguagePieChart data={languageData} loading={isFetchingLanguagePieChart} />
					</Box>
				</Paper>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 1, height: '100%' }}>
				<Paper
					elevation={0}
					sx={{ width: '45%', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<EngagementStatusByLanguageChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							authToken={authToken}
						/>
					</Box>
				</Paper>
				<Paper
					elevation={0}
					sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<EngagementFullDateChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							authToken={authToken}
						/>
					</Box>
				</Paper>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 1, height: '100%' }}>
				<Paper
					elevation={0}
					sx={{ width: '45%', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<ProviderFullDateChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							authToken={authToken}
						/>
					</Box>
				</Paper>
				<Paper
					elevation={0}
					sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<SurveyTrendFullDateChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							authToken={authToken}
						/>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};

const transformFilterValues = (values: FilterValuesArrays): FilterValuesStrings => ({
	duration: values.duration,
	districtIds: values.districts?.length ? values.districts.map((district: DistrictDto) => district._id).join(',') : '',
	engagementTypes: values.engagementType?.length ? values.engagementType.join(',') : '',
	schoolIds: values.schools?.length ? values.schools.map((school: FieldOptionInterface) => school._id).join(',') : '',
	serviceIds: values.services?.length
		? values.services.map((service: FieldOptionInterface) => service._id).join(',')
		: '',
	programIds: values.programs?.length
		? values.programs.map((program: FieldOptionInterface) => program._id).join(',')
		: '',
	languages: values.languages?.length ? values.languages.join(',') : '',
	providerIds: values.providers?.length ? values.providers.map((provider: ProviderDto) => provider._id).join(',') : '',
	engagementStatuses: values.engagementStatus?.length
		? values.engagementStatus.map((engagementStatus: EngagementStatusDto) => engagementStatus._id).join(',')
		: '',
});
